import { leftHeaderContainerStyle } from '@vivli/shared/components';
import { useDataRequestContext } from '@vivli/features/data-requests/infrastructure/context';
import { DisclosuresView } from '@vivli/features/disclosures/views';
import { useEffect, useMemo, useRef, useState } from 'react';
import { IDisclosure } from '@vivli/features/disclosures/infrastructure/interface';
import { DisclosureStatusEnum } from '@vivli/features/disclosures/infrastructure/enum';
import { useNavigate } from 'react-router';
import { useActiveUserContext, useDisclosuresApiContext, useModalApiContext, useToastApiContext } from '@vivli/core/infrastructure/context';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { useErrorHandling } from '@vivli/shared/infrastructure/hook';
import { finalize, first } from 'rxjs/operators';

export const DisclosuresContainerComponent = () => {
    const { dataRequest } = useDataRequestContext();
    const [disclosures, setDisclosures] = useState<IDisclosure[]>();
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const disclosuresService = useDisclosuresApiContext();
    const inAction = useRef<boolean>(false);
    const navigate = useNavigate();
    const { user, hasPermission } = useActiveUserContext();
    const { handleError } = useErrorHandling();
    const { info } = useToastApiContext();
    const modalService = useModalApiContext();

    const disclosureUri = useMemo(() => `/disclosure/${dataRequest.id}`, [dataRequest]);

    const handleRowClick = (disclosure: IDisclosure) => {
        if (inAction.current) {
            //don't propagate click if already in action
            inAction.current = false;
            return;
        }
        navigate(`${disclosureUri}/${disclosure.id}`);
    };

    const handleAddDisclosureClick = () => {
        if (isCreating) {
            return;
        }

        let sequenceNumber = 1;

        if (disclosures?.length > 0) {
            // get the latest sequence number, add 1 to notate it as the next disclosure
            const sequenceNumbers = disclosures.map((d) => d.sequenceNumber);
            sequenceNumber = Math.max(...sequenceNumbers) + 1;
        }

        const vivliDisclosureId = `${dataRequest.dataRequestDoi}-${sequenceNumber.toString().padStart(2, '0')}`;

        const newDisclosure: IDisclosure = {
            id: 0,
            vivliDisclosureId,
            dataRequestId: dataRequest.id,
            title: '',
            journalOrConferenceName: '',
            internalNote: '',
            status: DisclosureStatusEnum.Draft,
        };

        setIsCreating(true);
        disclosuresService
            .createDisclosure(newDisclosure)
            .pipe(
                first(),
                finalize(() => setIsCreating(false))
            )
            .subscribe({
                next: (created) => {
                    navigate(`${disclosureUri}/${created.id}`);
                },
                error: (error) => handleError('Unable to create disclosure.', error),
            });
    };

    const canAddDisclosure = useMemo(() => {
        const isOnResearchTeam = dataRequest?.researchTeam?.researchTeamDetails?.some((x) => x.userId === user.id);
        return user.isVivliAdmin || isOnResearchTeam;
    }, [user, dataRequest?.researchTeam?.researchTeamDetails]);

    const onDeleteClick = (disclosure: IDisclosure) => {
        inAction.current = true;
        modalService.confirm(`Are you sure you want to delete this disclosure -  ${disclosure.title}? This action cannot be undone.`, {
            title: 'Delete Confirmation.',
            confirmText: 'Ok',
            cancelText: 'Cancel',
            style: { minWidth: '45em', textAlign: 'left' },
            messageStyle: { textAlign: 'left' },
            onConfirm: () => {
                handleDeleteDisclosure(disclosure);
            },
            onCancel: () => {
                inAction.current = false;
            }
        });
    };

    const handleDeleteDisclosure = (disclosure: IDisclosure) => {
        disclosuresService
            .deleteDisclosure(disclosure.id)
            .pipe(
                first(),
                finalize(() => {
                    inAction.current = false;
                })
            )
            .subscribe({
                next: () => {
                    setDisclosures(disclosures?.filter((d) => d.id !== disclosure.id));
                    info('Disclosure deleted successfully.');
                },
                error: (error) => handleError('Unable to delete disclosure.', error)
            });
    };

    useEffect(() => {
        if (!dataRequest) {
            return;
        }

        disclosuresService.getDisclosures(dataRequest.id).subscribe({
            next: setDisclosures,
            error: (error) => {
                handleError('Unable to retrieve disclosures.', error);
                setDisclosures([]);
            }
        });
    }, [dataRequest]);

    return (
        <div style={leftHeaderContainerStyle}>
            {/*//this is reserved for the references component*/}
            {/*//coming in a future release*/}
            {/*<div style={FORM_ROW_SHARED}>*/}
            {/*    <div style={{width: '95%'}}>*/}
            {/*        <FieldHeaderComponent title={'Usage'} style={fieldHeaderStyle} />*/}
            {/*        <div style={Styles.CONTENT_BORDER}>*/}
            {/*            <DatasetUsageComponent studyId={studyId} studyMetadataDoi={studyMetadataDoi} studyPostedDate={studyPostedDate}/>*/}
            {/*        </div>*/}
            {/*    </div>*/}
            {/*</div>*/}

            <DisclosuresView
                disclosures={disclosures}
                canAddDisclosure={canAddDisclosure}
                onRowClick={handleRowClick}
                onAddDisclosureClick={handleAddDisclosureClick}
                isCreating={isCreating}
                canDeleteDisclosure={hasPermission(PermissionsEnum.DrActAdv)}
                onDelete={onDeleteClick}
            />
        </div>
    );
};
